import axios from 'axios';
import API_BACKEND_URL from '@/API_URL/api_back_end.js';
import { PICK_IMAGES_MAX_FILES } from '@/utils/imagePicker.js';

/** Fotos je Request — unter dem nginx-`proxy_read_timeout` von 300s bleiben. */
export const UPLOAD_IMAGE_CHUNK_SIZE = 5;

/**
 * @param {Array<File|Blob>} files
 * @param {number} [size]
 * @returns {Array<Array<File|Blob>>}
 */
export function chunkFiles(files, size = UPLOAD_IMAGE_CHUNK_SIZE) {
    const list = Array.from(files || []);
    const chunkSize = Math.max(1, size);
    const chunks = [];
    for (let i = 0; i < list.length; i += chunkSize) {
        chunks.push(list.slice(i, i + chunkSize));
    }
    return chunks;
}

/**
 * Zufällige Id für Upload-Wiederholungen (Server-Regex: 8–80 Zeichen `[A-Za-z0-9_-]`).
 * `randomUUID` fehlt ohne HTTPS (z. B. LAN-Test am Handy) und vor Safari 15.4.
 * @returns {string}
 */
export function newClientId() {
    if (typeof crypto === 'undefined') {
        return '';
    }
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    return '';
}

/**
 * @param {File|Blob} file
 * @returns {string}
 */
export function ensureUploadId(file) {
    if (!file) {
        return '';
    }
    if (!file.uploadId) {
        const id = newClientId();
        if (id) {
            file.uploadId = id;
        }
    }
    return file.uploadId || '';
}

function appendImageFiles(fd, files) {
    const list = Array.from(files || []);
    const ids = [];
    list.forEach((file, i) => {
        const name = (file && file.name) || `image${i + 1}.jpg`;
        fd.append('images', file, name);
        const id = ensureUploadId(file);
        if (id) {
            ids.push(id);
        }
    });
    if (ids.length === list.length && ids.length) {
        fd.append('uploadIds', JSON.stringify(ids));
    }
}

/**
 * Neues Protokoll anlegen (Bilder optional, sonst leer).
 * @param {Record<string, string>} fields
 * @param {Array<File|Blob>} [files]
 * @param {string} [clientBatchId]
 */
export async function createProtocolRecord(fields, files = [], clientBatchId) {
    const fd = new FormData();
    Object.entries(fields || {}).forEach(([key, value]) => {
        if (value != null && value !== '') {
            fd.append(key, value);
        }
    });
    if (clientBatchId) {
        fd.append('clientBatchId', clientBatchId);
    }
    appendImageFiles(fd, files);
    const response = await axios.post(
        `${API_BACKEND_URL}protokol/protokol_erfassen/upload`,
        fd,
    );
    return response.data;
}

/**
 * Bilder an ein bestehendes Protokoll anhängen (`action: uploadImages`).
 * @param {string} protocolId
 * @param {Array<File|Blob>} files
 */
export async function uploadImageChunk(protocolId, files) {
    const fd = new FormData();
    appendImageFiles(fd, files);
    fd.append('action', 'uploadImages');
    const response = await axios.put(
        `${API_BACKEND_URL}update_report/${protocolId}`,
        fd,
    );
    return response.data;
}

/**
 * @param {string} protocolId
 * @param {string} status
 */
export async function setProtocolStatus(protocolId, status) {
    await axios.put(
        `${API_BACKEND_URL}update_record_status/${protocolId}/${status}`,
    );
}

/**
 * Protokoll anlegen (falls nötig) und Bilder in Blöcken hochladen.
 * Bei einem fehlgeschlagenen Block bleiben bereits gesendete Dateien auf dem Server;
 * `remainingFiles` sind die noch nicht angekommenen.
 *
 * @param {object} options
 * @param {string} [options.protocolId]
 * @param {Record<string, string>} options.fields
 * @param {Array<File|Blob>} options.files
 * @param {string} [options.finalStatus]
 * @param {string} [options.clientBatchId]
 * @param {(done: number, total: number, label: string) => void} [options.onProgress]
 * @returns {Promise<{
 *   protocolId: string,
 *   protocol: object|null,
 *   uploadedFiles: Array<File|Blob>,
 *   remainingFiles: Array<File|Blob>,
 *   complete: boolean,
 *   statusFailed: boolean,
 * }>}
 */
export async function uploadProtocolWithImageChunks({
    protocolId,
    fields,
    files,
    finalStatus,
    onProgress,
    clientBatchId,
} = {}) {
    const allFiles = Array.from(files || []);
    allFiles.forEach((file) => ensureUploadId(file));
    const total = allFiles.length;
    let id = protocolId;
    let protocol = null;
    let offset = 0;

    const report = (done, label) => {
        if (typeof onProgress === 'function') {
            onProgress(done, Math.max(total, 1), label);
        }
    };

    const chunks = chunkFiles(allFiles, UPLOAD_IMAGE_CHUNK_SIZE);
    let pendingChunks = chunks;

    if (!id) {
        report(0, total ? 'Protokoll anlegen…' : 'Protokoll speichern…');
        const createFields = { ...(fields || {}) };
        if (finalStatus === 'Abgeschlossen') {
            createFields.status = 'In Bearbeitung';
            delete createFields.dtClose;
        }
        const firstChunk = chunks[0] || [];
        protocol = await createProtocolRecord(createFields, firstChunk, clientBatchId);
        id = protocol && protocol._id;
        // Der erste Block ist mit dem Anlegen erledigt, auch Dateien, die der Server
        // als ungültig verworfen hat. Erneut senden würde sie nur noch einmal ablehnen.
        offset = firstChunk.length;
        pendingChunks = chunks.slice(1);
        if (offset) {
            report(offset, `Bilder ${offset} von ${total}…`);
        }
    }

    if (!id) {
        throw new Error('Protokoll wurde nicht angelegt');
    }

    try {
        for (const chunk of pendingChunks) {
            if (!chunk.length) {
                continue;
            }
            const from = offset + 1;
            const to = offset + chunk.length;
            report(offset, `Bilder ${from}–${to} von ${total}…`);
            const data = await uploadImageChunk(id, chunk);
            protocol = Array.isArray(data) ? data[1] : data;
            offset += chunk.length;
            report(offset, `Bilder ${offset} von ${total}…`);
        }
    } catch (err) {
        return {
            protocolId: id,
            protocol,
            uploadedFiles: allFiles.slice(0, offset),
            remainingFiles: allFiles.slice(offset),
            complete: false,
            statusFailed: false,
            error: err,
        };
    }

    if (finalStatus === 'Abgeschlossen') {
        report(total, 'Protokoll abschließen…');
        try {
            await setProtocolStatus(id, 'Abgeschlossen');
        } catch (err) {
            return {
                protocolId: id,
                protocol,
                uploadedFiles: allFiles,
                remainingFiles: [],
                complete: false,
                statusFailed: true,
                error: err,
            };
        }
    }

    return {
        protocolId: id,
        protocol,
        uploadedFiles: allFiles,
        remainingFiles: [],
        complete: true,
        statusFailed: false,
    };
}

/**
 * Bilder in Blöcken an ein bestehendes Protokoll anhängen.
 *
 * @param {object} options
 * @param {string} options.protocolId
 * @param {Array<File|Blob>} options.files
 * @param {(done: number, total: number, label: string) => void} [options.onProgress]
 * @returns {Promise<{
 *   protocol: object|null,
 *   uploadedFiles: Array<File|Blob>,
 *   remainingFiles: Array<File|Blob>,
 *   complete: boolean,
 * }>}
 */
export async function appendImagesInChunks({
    protocolId,
    files,
    onProgress,
} = {}) {
    const allFiles = Array.from(files || []);
    const total = allFiles.length;
    let protocol = null;
    let offset = 0;

    try {
        for (const chunk of chunkFiles(allFiles, UPLOAD_IMAGE_CHUNK_SIZE)) {
            if (!chunk.length) {
                continue;
            }
            const from = offset + 1;
            const to = offset + chunk.length;
            if (typeof onProgress === 'function') {
                onProgress(offset, total, `Bilder ${from}–${to} von ${total}…`);
            }
            const data = await uploadImageChunk(protocolId, chunk);
            protocol = Array.isArray(data) ? data[1] : data;
            offset += chunk.length;
            if (typeof onProgress === 'function') {
                onProgress(offset, total, `Bilder ${offset} von ${total}…`);
            }
        }
    } catch (err) {
        return {
            protocol,
            uploadedFiles: allFiles.slice(0, offset),
            remainingFiles: allFiles.slice(offset),
            complete: false,
            error: err,
        };
    }

    return {
        protocol,
        uploadedFiles: allFiles.slice(0, offset),
        remainingFiles: [],
        complete: true,
    };
}

/**
 * Multer-Ablehnung als kurze Nutzerzeile. Leer, wenn der Fehler woanders liegt.
 * @param {unknown} error
 * @returns {string}
 */
export function messageForUploadError(error) {
    const data = error?.response?.data;
    const errCode = data?.err_code;
    if (errCode === 'LIMIT_FILE_COUNT') {
        return `Bitte maximal ${PICK_IMAGES_MAX_FILES} Bilder auf einmal hochladen.`;
    }
    if (errCode === 'LIMIT_FILE_SIZE') {
        return 'Eine Datei ist zu groß (max. 20 MB).';
    }
    if (errCode === 'LIMIT_FILE_TYPE') {
        return data?.err_message || 'Nur Bilddateien sind erlaubt.';
    }
    return '';
}
